import { appendSpeechText } from './append-speech-text.js';

// Chrome and Safari ship the Web Speech API, but TypeScript's DOM library doesn't describe it.
interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface RecognitionResultEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

interface RecognitionErrorEvent {
  error: string;
}

type SpeechRecognitionConstructor = new () => Recognition;

declare global {
  interface Window {
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export type SpeechToTextStatus = 'unsupported' | 'idle' | 'listening' | 'error';

export type SpeechToTextController = {
  readonly status: SpeechToTextStatus;
  readonly errorMessage: string | null;
  readonly isSupported: boolean;
  start: (
    existingText: string,
    onTextChange: (nextText: string) => void,
    onStatusChange?: (status: SpeechToTextStatus, errorMessage: string | null) => void
  ) => void;
  stop: () => void;
  dispose: () => void;
};

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const w = window as Window & { SpeechRecognition?: SpeechRecognitionConstructor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechToTextSupported(): boolean {
  return getSpeechRecognitionConstructor() != null;
}

export function createSpeechToTextController(options?: { lang?: string }): SpeechToTextController {
  const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
  const isSupported = SpeechRecognitionCtor != null;

  let status: SpeechToTextStatus = isSupported ? 'idle' : 'unsupported';
  let errorMessage: string | null = null;
  let recognition: Recognition | null = null;
  let baseText = '';
  let onTextChange: ((nextText: string) => void) | null = null;
  let onStatusChange: ((status: SpeechToTextStatus, errorMessage: string | null) => void) | null = null;

  function setStatus(next: SpeechToTextStatus, message: string | null = null) {
    status = next;
    errorMessage = message;
    onStatusChange?.(status, errorMessage);
  }

  function cleanupRecognition() {
    if (!recognition) {
      return;
    }

    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition = null;
  }

  return {
    get status() {
      return status;
    },
    get errorMessage() {
      return errorMessage;
    },
    get isSupported() {
      return isSupported;
    },
    start(existingText, onChange, statusChange) {
      if (!SpeechRecognitionCtor) {
        setStatus('unsupported', 'Speech recognition is not supported in this browser.');
        return;
      }

      if (status === 'listening') {
        return;
      }

      baseText = existingText;
      onTextChange = onChange;
      onStatusChange = statusChange ?? null;
      setStatus('listening', null);

      const instance = new SpeechRecognitionCtor();
      instance.continuous = true;
      instance.interimResults = true;
      instance.lang = options?.lang ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-AU');

      instance.onresult = (event: RecognitionResultEvent) => {
        let sessionTranscript = '';
        for (let i = 0; i < event.results.length; i += 1) {
          sessionTranscript += event.results[i]?.[0]?.transcript ?? '';
        }

        const nextText = appendSpeechText(baseText, sessionTranscript);
        onTextChange?.(nextText);
      };

      instance.onerror = (event: RecognitionErrorEvent) => {
        if (event.error === 'aborted' || event.error === 'no-speech') {
          setStatus('idle', null);
          return;
        }

        setStatus('error', 'Could not capture speech. Check your microphone permissions and try again.');
        cleanupRecognition();
      };

      instance.onend = () => {
        if (status === 'listening') {
          setStatus('idle', null);
        }
        cleanupRecognition();
      };

      recognition = instance;

      try {
        instance.start();
      } catch {
        setStatus('error', 'Could not start speech recognition.');
        cleanupRecognition();
      }
    },
    stop() {
      if (!recognition) {
        if (status === 'listening') {
          setStatus('idle', null);
        }
        return;
      }

      try {
        recognition.stop();
      } catch {
        /* ignore */
      }

      setStatus('idle', null);
      cleanupRecognition();
    },
    dispose() {
      if (recognition) {
        try {
          recognition.abort();
        } catch {
          /* ignore */
        }
      }

      cleanupRecognition();
      onTextChange = null;
      onStatusChange = null;
      setStatus(isSupported ? 'idle' : 'unsupported', null);
    },
  };
}
